import { useMemo } from "react";

import { AIIcon } from "@/assets/icons";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { AnalysisJobAreaInputType, AnalysisJobItem } from "@/types/analysis";
import { AnalysisResultType } from "@/types/dataset";

import { ProjectCard } from "../spatialAnalysis/projectList/projectCard";
import TimeAgo from "../ui/TimeAgo";

interface HistoryCardProps {
  data: AnalysisJobItem;
  isSelected?: boolean;
  onSelect?: (id: string) => void;
}

export const HistoryCard = ({
  data,
  isSelected = false,
  onSelect
}: HistoryCardProps) => {
  const { isAnalysisChatLinkEnabled } = useAppFeatures();
  const datasetCount = data.datasets?.length || 0;
  const isProfiling =
    data.output_type === AnalysisResultType.TYPE_SITE_PROFILING;
  const isAiGenerated = isAnalysisChatLinkEnabled && !!data.conversation_id;

  const outputText = useMemo(() => {
    if (!data?.output_type) return "-";
    return isProfiling ? "Profiling" : "Grid";
  }, [data?.output_type, isProfiling]);

  const typeText = useMemo(() => {
    if (!data?.input_type) return "-";
    return data.input_type === AnalysisJobAreaInputType.ADMIN
      ? "Adm Area"
      : "Catchment";
  }, [data?.input_type]);

  return (
    <ProjectCard
      className="w-full text-left rounded-lg h-auto border bg-white p-2 group transition-all"
      dataTestid="history-card"
      title={data.name || `Spatial Analysis (${data.id})`}
      subtitle={
        <>
          <TimeAgo date={data.created_at} />
          {data.created_by && <span> - by {data.created_by}</span>}
        </>
      }
      headerAction={
        isAiGenerated ? (
          <AIIcon
            width={16}
            height={16}
            className="fill-primary-teal shrink-0"
            title="Generated by AI Analyst"
          />
        ) : undefined
      }
      datasetCount={datasetCount}
      areaInputType={typeText}
      outputType={outputText}
      isProfiling={isProfiling}
      isSelected={isSelected}
      onClick={() => onSelect?.(data.id)}
    />
  );
};
